import { IdentityApi } from '@backstage/core-plugin-api';

export const REAUTH_PATH_STORAGE_KEY = 'pdp.reauth.returnPath';

const stripBasename = (path: string, basename: string): string => {
  const base = basename.replace(/\/+$/, '');
  if (!base) {
    return path;
  }
  if (path === base) {
    return '/';
  }
  return path.startsWith(`${base}/`) ? path.slice(base.length) : path;
};

const hasControlChars = (value: string): boolean =>
  Array.from(value).some(char => char.charCodeAt(0) < 0x20);

/** Accepts only same-origin relative paths such as `/catalog?a=b#c`. */
export const isSafeRelativePath = (value: unknown): value is string =>
  typeof value === 'string' &&
  value.startsWith('/') &&
  !value.startsWith('//') &&
  !value.includes('\\') &&
  !hasControlChars(value);

export const storeCurrentPath = (basename = ''): void => {
  try {
    const { pathname, search, hash } = window.location;
    const path = `${stripBasename(pathname, basename)}${search}${hash}`;
    if (isSafeRelativePath(path)) {
      window.sessionStorage.setItem(REAUTH_PATH_STORAGE_KEY, path);
    }
  } catch {
    // sessionStorage can be unavailable; fall back to the default landing page
  }
};

/** Returns the stored path once and removes it from storage. */
export const consumeStoredPath = (): string | undefined => {
  try {
    const value = window.sessionStorage.getItem(REAUTH_PATH_STORAGE_KEY);
    window.sessionStorage.removeItem(REAUTH_PATH_STORAGE_KEY);
    return isSafeRelativePath(value) ? value : undefined;
  } catch {
    return undefined;
  }
};

export const withReturnPathOnSignOut = (
  identityApi: IdentityApi,
  basename = '',
): IdentityApi => ({
  getProfileInfo: () => identityApi.getProfileInfo(),
  getBackstageIdentity: () => identityApi.getBackstageIdentity(),
  getCredentials: () => identityApi.getCredentials(),
  signOut: async () => {
    storeCurrentPath(basename);
    return identityApi.signOut();
  },
});
